import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../context/FamilyContext';
import { learnerService, WeeklyDigest } from '../../services/learnerService';

// Same under-claiming vocabulary as MasteryMap.tsx/SkillDetail.tsx - no
// stronger claim-of-completion word may render anywhere in this file. See
// IMPLEMENTATION_PLAN.md §3.2 / PHASE_PROMPTS.md's Phase 6 entry.
const STATUS_LABEL: Record<string, string> = {
  'not-attempted': 'Not started yet',
  emerging: 'Just getting started',
  developing: 'Making progress',
  secure: "Looks solid - let's recheck in two weeks",
};

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success'> = {
  'not-attempted': 'default',
  emerging: 'warning',
  developing: 'info',
  secure: 'success',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * The weekly digest view (Phase 6). Deliberately deterministic and
 * evidence-led - no LLM-generated narrative, no adjectives without a count
 * behind them. See db_backend/services/digestService.js for how every field
 * here is derived.
 */
const WeeklyProgress: React.FC = () => {
  const navigate = useNavigate();
  const { learners, loading: familyLoading } = useFamily();
  const learner = learners[0] || null;

  const [digest, setDigest] = useState<WeeklyDigest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!learner) return;
    const token = localStorage.getItem('token') || '';
    setLoading(true);
    setError(null);
    try {
      const data = await learnerService.getDigest(learner._id, token);
      setDigest(data.digest);
    } catch (err) {
      console.error('Error fetching weekly digest:', err);
      setError('Unable to load the weekly summary right now.');
    } finally {
      setLoading(false);
    }
  }, [learner]);

  useEffect(() => {
    load();
  }, [load]);

  if (familyLoading || loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!learner) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Add a child to see a weekly summary.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button variant="text" onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        &larr; Back to Skill Map
      </Button>

      <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
        {learner.firstName}'s Week
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

      {digest && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {formatDate(digest.since)} &ndash; {formatDate(digest.until)}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2.5, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{digest.itemsAttempted}</Typography>
              <Typography variant="body2" color="text.secondary">items attempted</Typography>
            </Paper>
            <Paper sx={{ p: 2.5, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{digest.activeDays}</Typography>
              <Typography variant="body2" color="text.secondary">
                {digest.activeDays === 1 ? 'day active' : 'days active'}
              </Typography>
            </Paper>
          </Box>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>What changed this week</Typography>
            {digest.transitions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No status changes this week - keep practicing to see progress here.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {digest.transitions.map((t) => (
                  <Box key={t.skillId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body1">{t.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={STATUS_LABEL[t.statusBefore]} size="small" color={STATUS_COLOR[t.statusBefore]} variant="outlined" />
                      <Typography variant="body2" color="text.secondary">&rarr;</Typography>
                      <Chip label={STATUS_LABEL[t.statusAfter]} size="small" color={STATUS_COLOR[t.statusAfter]} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {digest.overridesInPeriod.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Your corrections this week</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {digest.overridesInPeriod.map((o, i) => (
                  <Box key={`${o.skillId}-${i}`}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body1">{o.name || 'Unknown skill'}</Typography>
                      <Chip label={STATUS_LABEL[o.status]} size="small" color={STATUS_COLOR[o.status]} />
                    </Box>
                    {o.note && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        "{o.note}"
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>What's next</Typography>
            {digest.topNextSkills.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nothing new to suggest yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {digest.topNextSkills.map((rec) => (
                  <React.Fragment key={rec.skillId}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{rec.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{rec.reason}</Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/dashboard/skills/${rec.skillId}?learnerId=${learner._id}`)}
                      >
                        Work on this
                      </Button>
                    </Box>
                    <Divider sx={{ '&:last-child': { display: 'none' } }} />
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default WeeklyProgress;
